import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdElevatedInputSx,
  ipdPageSx,
  getIpdSelectMenuProps,
  IpdButton,
  IpdPageHeader,
  IpdSectionCard,
  IpdTabBar,
  tableContainerSx,
} from "../../../ipd/utils";
import { EmergencyPlaceholder } from "../../components/EmergencyBadges";
import EdServicesSection from "./EdServicesSection";
import {
  addBed,
  addBedCategory,
  addInjuryTerm,
  deactivateBedCategory,
  getEmergencySetup,
  removeBed,
  removeInjuryTerm,
  updateBed,
  setBedCategoryCharge,
  updateBedCategory,
} from "../../api/emergencyApi";

const TABS = [
  { id: "beds", label: "Beds & categories" },
  { id: "lists", label: "Injury lists" },
  { id: "services", label: "ED services & fees" },
];

const BED_STATUS = {
  free: { label: "Free", ...IPD_COLORS.success },
  occupied: { label: "Occupied", ...IPD_COLORS.error },
  cleaning: { label: "Being cleaned", ...IPD_COLORS.warning },
};

function BedStatusChip({ status }) {
  const s = BED_STATUS[status] || BED_STATUS.free;
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 600,
        bgcolor: s.bg,
        color: s.text,
        border: `1px solid ${s.border}`,
      }}
    />
  );
}

/** The hospital's existing rule, shown read-only — it is not an ED setting. */
function HousekeepingRule({ housekeeping }) {
  const enabled = housekeeping?.auto_housekeeping;
  return (
    <IpdSectionCard
      title="Housekeeping rule"
      subtitle="Inherited from the hospital's inpatient setting — not a separate emergency rule"
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: IPD_COLORS.info.text, mt: 0.25 }} />
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
            {enabled
              ? "A freed emergency bed goes to Being cleaned."
              : "A freed emergency bed goes straight back to Free."}
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: IPD_COLORS.textSecondary }}>
            Emergency beds follow exactly the behaviour already configured for inpatient beds,
            so the two can never disagree. Change it under Housekeeping Settings, where it is
            owned.
          </Typography>
          <Typography sx={{ mt: 1, fontSize: 12.5, color: IPD_COLORS.textMuted }}>
            An emergency bed is only ever <strong>free</strong>, <strong>occupied</strong> or{" "}
            <strong>being cleaned</strong>. There is no hold or reservation.
          </Typography>
        </Box>
      </Box>
    </IpdSectionCard>
  );
}

/** Rupees, or null when this category has no price yet. */
function chargeLabel(charge) {
  const fee = Number(charge?.fee);
  if (!charge || !Number.isFinite(fee)) return null;
  return `₹${fee.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function BedCategories({ categories, onAdd, onRename, onToggle, onSetCharge, busy }) {
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  // Which category's price is being edited, and the amount typed so far.
  const [pricingId, setPricingId] = useState(null);
  const [fee, setFee] = useState("");

  const openPricing = (c) => {
    setPricingId(c.id);
    setFee(c.charge?.fee != null ? String(Number(c.charge.fee)) : "");
  };

  const savePricing = (c) => {
    const amount = fee.trim();
    if (amount === "" || Number(amount) < 0) return;
    onSetCharge(c, amount);
    setPricingId(null);
  };

  const submitNew = () => {
    const n = newName.trim();
    if (!n) return;
    onAdd(n);
    setNewName("");
  };

  return (
    <IpdSectionCard
      title="Bed categories"
      subtitle="Each hospital names its own — add, rename or switch one off without a software change"
    >
      {categories.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          No categories yet. Add the first one below — beds cannot be created until one exists.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {categories.map((c) => (
            <Box
              key={c.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 1,
                border: `1px solid ${IPD_COLORS.border}`,
                borderRadius: `${IPD_SHAPE.radiusSm}px`,
              }}
            >
              {editingId === c.id ? (
                <>
                  <TextField
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    size="small"
                    autoFocus
                    sx={{ ...ipdElevatedInputSx, flex: 1 }}
                    inputProps={{ "aria-label": "Category name" }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && editName.trim()) {
                        onRename(c.id, editName.trim());
                        setEditingId(null);
                      }
                      if (e.key === "Escape") setEditingId(null);
                    }}
                  />
                  <IpdButton
                    size="small"
                    disabled={!editName.trim()}
                    onClick={() => {
                      onRename(c.id, editName.trim());
                      setEditingId(null);
                    }}
                  >
                    Save
                  </IpdButton>
                  <IpdButton size="small" variant="outlined" onClick={() => setEditingId(null)}>
                    Cancel
                  </IpdButton>
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      flex: 1,
                      fontSize: 14,
                      fontWeight: 600,
                      color: c.active ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted,
                      textDecoration: c.active ? "none" : "line-through",
                    }}
                  >
                    {c.name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                    {c.bed_count ?? 0} bed{(c.bed_count ?? 0) === 1 ? "" : "s"}
                  </Typography>

                  {/* The price a stay in this category is billed at. Shown on
                      the category rather than the bed because every bed of a
                      kind costs the same, which is how the catalogue links it. */}
                  {pricingId === c.id ? (
                    <>
                      <TextField
                        value={fee}
                        onChange={(e) => setFee(e.target.value)}
                        size="small"
                        autoFocus
                        type="number"
                        placeholder="Amount"
                        sx={{ ...ipdElevatedInputSx, width: 120 }}
                        InputProps={{ startAdornment: <Box sx={{ mr: 0.5 }}>₹</Box> }}
                        inputProps={{ "aria-label": `Charge for ${c.name}`, min: 0 }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") savePricing(c);
                          if (e.key === "Escape") setPricingId(null);
                        }}
                      />
                      <IpdButton
                        size="small"
                        disabled={fee.trim() === "" || Number(fee) < 0 || busy}
                        onClick={() => savePricing(c)}
                      >
                        Save
                      </IpdButton>
                      <IpdButton size="small" variant="outlined" onClick={() => setPricingId(null)}>
                        Cancel
                      </IpdButton>
                    </>
                  ) : (
                    <Tooltip
                      title={
                        c.charge
                          ? `${c.charge.service_code} — click to change the price`
                          : "No charge set — a bed of this category cannot be billed yet"
                      }
                    >
                      <Box
                        component="button"
                        type="button"
                        onClick={() => openPricing(c)}
                        aria-label={`Set charge for ${c.name}`}
                        sx={{
                          border: `1px solid ${IPD_COLORS.border}`,
                          borderRadius: `${IPD_SHAPE.radiusSm}px`,
                          bgcolor: "transparent",
                          cursor: "pointer",
                          px: 1,
                          py: 0.35,
                          fontSize: 12.5,
                          fontWeight: 700,
                          fontFamily: "inherit",
                          color: c.charge ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted,
                          "&:hover": { borderColor: IPD_COLORS.borderHover },
                        }}
                      >
                        {chargeLabel(c.charge) || "Set charge"}
                      </Box>
                    </Tooltip>
                  )}

                  <Tooltip title="Rename">
                    <IconButton
                      size="small"
                      aria-label={`Rename ${c.name}`}
                      onClick={() => {
                        setEditingId(c.id);
                        setEditName(c.name);
                      }}
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={c.active ? "Switch off" : "Switch on"}>
                    <Switch
                      size="small"
                      checked={c.active}
                      disabled={busy}
                      onChange={() => onToggle(c)}
                      inputProps={{ "aria-label": `${c.name} active` }}
                    />
                  </Tooltip>
                </>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="New category, e.g. Resuscitation Bed"
          size="small"
          sx={{ ...ipdElevatedInputSx, flex: 1 }}
          inputProps={{ "aria-label": "New bed category" }}
          onKeyDown={(e) => e.key === "Enter" && submitNew()}
        />
        <IpdButton startIcon={<AddIcon />} onClick={submitNew} disabled={!newName.trim() || busy}>
          Add
        </IpdButton>
      </Box>
    </IpdSectionCard>
  );
}

function Beds({ beds, categories, onAdd, onRemove, onMarkClean, busy }) {
  const [label, setLabel] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const activeCategories = categories.filter((c) => c.active);

  const submit = () => {
    if (!label.trim() || !categoryId) return;
    onAdd({ label: label.trim(), categoryId });
    setLabel("");
  };

  return (
    <IpdSectionCard
      title="Individual beds"
      subtitle="Each physical bed belongs to exactly one category"
    >
      {beds.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          No beds yet. Beds added here become available to assign to patients.
        </Typography>
      ) : (
        <Box sx={{ ...tableContainerSx, maxHeight: 340, mb: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Bed</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {beds.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{b.label}</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>{b.category_name}</TableCell>
                  <TableCell><BedStatusChip status={b.status} /></TableCell>
                  <TableCell align="right">
                    {b.status === "cleaning" && (
                      <Tooltip title="Mark cleaned">
                        <IconButton
                          size="small"
                          disabled={busy}
                          aria-label={`Mark ${b.label} cleaned`}
                          onClick={() => onMarkClean(b)}
                        >
                          <CleaningServicesIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={b.status === "free" ? "Remove bed" : "Only a free bed can be removed"}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busy || b.status !== "free"}
                          aria-label={`Remove ${b.label}`}
                          onClick={() => onRemove(b)}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <TextField
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Bed label, e.g. RESUS-01"
          size="small"
          sx={{ ...ipdElevatedInputSx, flex: 1, minWidth: 180 }}
          inputProps={{ "aria-label": "New bed label" }}
        />
        <TextField
          select
          value={categoryId}
          onChange={(e) => setCategoryId(e.target.value)}
          size="small"
          sx={{ ...ipdElevatedInputSx, minWidth: 200 }}
          SelectProps={{ displayEmpty: true, MenuProps: getIpdSelectMenuProps() }}
          inputProps={{ "aria-label": "Bed category" }}
        >
          <MenuItem value="" disabled>
            {activeCategories.length ? "Select category" : "Add a category first"}
          </MenuItem>
          {activeCategories.map((c) => (
            <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
          ))}
        </TextField>
        <IpdButton
          startIcon={<AddIcon />}
          onClick={submit}
          disabled={!label.trim() || !categoryId || busy}
        >
          Add bed
        </IpdButton>
      </Box>
    </IpdSectionCard>
  );
}

function TermEditor({ title, subtitle, placeholder, terms, onAdd, onRemove, busy }) {
  const [value, setValue] = useState("");
  const submit = () => {
    const v = value.trim();
    if (!v) return;
    onAdd(v);
    setValue("");
  };

  return (
    <IpdSectionCard title={title} subtitle={subtitle}>
      {terms.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary, mb: 2 }}>
          Nothing on this list yet — add the terms your injury reports should offer.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {terms.map((t) => (
            <Chip
              key={t.id}
              label={t.name}
              onDelete={busy ? undefined : () => onRemove(t)}
              deleteIcon={<CloseIcon />}
              sx={{
                bgcolor: IPD_COLORS.selectedBg,
                color: IPD_COLORS.primary,
                border: `1px solid ${IPD_COLORS.border}`,
                fontWeight: 500,
              }}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          size="small"
          sx={{ ...ipdElevatedInputSx, flex: 1 }}
          inputProps={{ "aria-label": `Add to ${title}` }}
          onKeyDown={(e) => e.key === "Enter" && submit()}
        />
        <IpdButton startIcon={<AddIcon />} onClick={submit} disabled={!value.trim() || busy}>
          Add
        </IpdButton>
      </Box>
    </IpdSectionCard>
  );
}

export default function DepartmentSetup() {
  const [tab, setTab] = useState("beds");
  const [setup, setSetup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSetup(await getEmergencySetup());
      setError("");
    } catch (e) {
      setError(e?.message || "Could not load the department setup");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Every mutation reloads the whole payload, so bed counts and category
   *  states can never drift out of step with what was just changed. */
  const run = async (fn, successMessage) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      setNotice(successMessage);
      await load();
    } catch (e) {
      setError(e?.message || "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const categories = setup?.bed_categories || [];
  const beds = setup?.beds || [];

  return (
    <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
      <IpdPageHeader
        eyebrow="Emergency Department"
        title="Department Setup"
        description="One-time configuration — bed categories, beds and the injury report's vocabulary."
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          onClose={() => setError("")}
          action={<IpdButton variant="outlined" size="small" onClick={load}>Retry</IpdButton>}
        >
          {error}
        </Alert>
      )}
      {notice && (
        <Alert
          severity="success"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          onClose={() => setNotice("")}
        >
          {notice}
        </Alert>
      )}

      <IpdTabBar tabs={TABS} activeTab={tab} onChange={setTab} ariaLabel="Department setup sections" />

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="rounded" height={200} sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }} />
          ))}
        </Box>
      ) : !setup ? (
        <EmergencyPlaceholder
          title="Setup unavailable"
          description="The department configuration could not be loaded."
          action={<IpdButton onClick={load}>Try again</IpdButton>}
        />
      ) : tab === "beds" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <HousekeepingRule housekeeping={setup.housekeeping} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
            <BedCategories
              categories={categories}
              busy={busy}
              onAdd={(name) => run(() => addBedCategory(name), "Bed category added.")}
              onRename={(id, name) => run(() => updateBedCategory(id, { name }), "Bed category renamed.")}
              onToggle={(c) =>
                run(
                  () => (c.active ? deactivateBedCategory(c.id) : updateBedCategory(c.id, { active: true })),
                  c.active ? "Bed category switched off." : "Bed category switched on."
                )
              }
              onSetCharge={(c, fee) =>
                run(
                  () => setBedCategoryCharge(c.id, { fee }),
                  c.charge ? "Bed charge updated." : "Bed charge added."
                )
              }
            />
            <Beds
              beds={beds}
              categories={categories}
              busy={busy}
              onAdd={(payload) => run(() => addBed(payload), "Bed created.")}
              onRemove={(b) => run(() => removeBed(b.id), "Bed removed.")}
              onMarkClean={(b) => run(() => updateBed(b.id, { status: "free" }), "Bed marked clean and available.")}
            />
          </Box>
        </Box>
      ) : tab === "services" ? (
        <EdServicesSection />
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
          <TermEditor
            title="Body parts"
            subtitle="The location choices offered by the injury report"
            placeholder="e.g. Left shoulder"
            terms={setup.body_parts || []}
            busy={busy}
            onAdd={(name) => run(() => addInjuryTerm("body-parts", name), "Body part added.")}
            onRemove={(t) => run(() => removeInjuryTerm("body-parts", t.id), "Body part removed.")}
          />
          <TermEditor
            title="Wound types"
            subtitle="The wound-type choices offered by the injury report"
            placeholder="e.g. Puncture wound"
            terms={setup.wound_types || []}
            busy={busy}
            onAdd={(name) => run(() => addInjuryTerm("wound-types", name), "Wound type added.")}
            onRemove={(t) => run(() => removeInjuryTerm("wound-types", t.id), "Wound type removed.")}
          />
        </Box>
      )}

      {!loading && setup && tab === "lists" && (
        <Typography sx={{ mt: 2.5, fontSize: 12, color: IPD_COLORS.textMuted }}>
          Removing a term takes it off the list for new injuries. Reports already recorded against
          it keep reading correctly, and re-adding the same term restores the original entry.
        </Typography>
      )}
    </Box>
  );
}
